import { useId } from "react";
import { Pointer } from "rfc6902";
import { Control210, Control211 } from "../controls";
import { useBeFigure } from "./BeFigureContextProvider";
import { PATCH_FIGURE_STATE } from "./functions/interface";
import { FigureVarType } from "./types/FigureVarSettings";

export interface FigureVarProps {
  pointer: string;
  label: string;
  figureVarType: FigureVarType;
  varName: string;
  options?: string[];
}

/**
 * FigureVar component to display a variable from the figure context
 *
 * @param props - FigureVarProps
 * @param props.figureVarType - The type of the variable
 * @param props.label - The label of the variable
 * @param props.options - The options for select type variables
 * @param props.pointer - The location of the variable in the data
 * @param props.varName - The variable name
 * @returns The FigureVar component
 */
export const FigureVar = ({
  label,
  figureVarType,
  pointer,
  varName,
  options,
}: FigureVarProps): React.ReactNode => {
  const id = useId();
  const { state, dispatch } = useBeFigure();
  const figureInput = state?.figureInput;

  const dp = Pointer.fromJSON(pointer);
  const val = dp.get(figureInput) ?? "";

  return !state ? null : figureVarType === FigureVarType.select ? (
    <Control211
      key={`${id}-${varName}`}
      id={`${id}-${varName}`}
      label={label}
      labelWidth={3}
      value={val}
      setValue={(newValue) =>
        dispatch({
          operation: PATCH_FIGURE_STATE,
          pointer,
          value: newValue,
        })
      }
      options={[
        ...(!options?.includes(val) ? [{ value: "", label: "-- Select --" }] : []),
        ...(options ?? []).map((s) => ({
          value: s,
          label: s,
        })),
      ]}
    />
  ) : (
    <Control210
      key={`${id}-${varName}`}
      id={`${id}-${varName}`}
      label={label}
      labelWidth={3}
      value={val}
      type={figureVarType === FigureVarType.number ? "number" : "string"}
      debounceMilliseconds={-1}
      setValue={(newValue) =>
        dispatch({
          operation: PATCH_FIGURE_STATE,
          pointer,
          value: newValue,
        })
      }
    />
  );
};

FigureVar.displayName = "FigureVar";
